import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';

import { useCreateCategory } from '@/api/category/use-create-category.ts';
import type { CreateCategoryDto } from '@/api/types';
import FormInput from '@/components/form/form-input/form-input.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const createCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Category name is required')
    .max(50, 'Category name must be less than 50 characters'),
});

type CreateCategoryModalProps = {
  onClose: () => void;
};

const CreateCategoryModal = ({ onClose }: CreateCategoryModalProps) => {
  const { projectId } = useParams();

  const form = useForm<CreateCategoryDto>({
    resolver: zodResolver(createCategorySchema),
  });

  const { mutateAsync: createCategory, isPending } = useCreateCategory(projectId!);

  const onSubmit = (data: CreateCategoryDto) => {
    createCategory(data)
      .then(() => {
        form.reset();
        onClose();
      })
      .catch(() => undefined);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Category</DialogTitle>
            <DialogDescription>
              All expenses can be assigned to a category. Within category you can track your
              expenses on a more detailed level. You can create as many categories as you want and
              assign them to your expenses.
            </DialogDescription>
          </DialogHeader>
          <FormInput name="name" label="Name" />
          <DialogFooter>
            <Button onClick={() => onClose()} variant="outline">
              Cancel
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type="submit">
              Create category
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};

export default CreateCategoryModal;
